import React, { useState } from 'react';
import {
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonToggle,
  IonAlert,
} from '@ionic/react';
import {
  sunnyOutline,
  moonOutline,
  contrastOutline,
} from 'ionicons/icons';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';

type Theme = 'light' | 'dark' | 'system';

const ThemeToggle: React.FC<{ variant?: 'toggle' | 'select' | 'button' }> = ({
  variant = 'toggle',
}) => {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { t } = useLanguage();
  const [showAlert, setShowAlert] = useState(false);

  const handleToggleChange = (checked: boolean) => {
    setTheme(checked ? 'dark' : 'light');
  };

  const handleSelectChange = (e: { detail: { value: string } }) => {
    setTheme(e.detail.value as Theme);
  };

  const handleThemeInfo = () => {
    setShowAlert(true);
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return sunnyOutline;
      case 'dark':
        return moonOutline;
      default:
        return contrastOutline;
    }
  };

  
  if (variant === 'toggle') {
    return (
      <>
        <IonItem>
          <IonIcon
            icon={getThemeIcon()}
            style={{ marginRight: '16px', color: 'var(--color-text-secondary)' }}
          />
          <IonLabel>{t('darkMode') || 'Dark Mode'}</IonLabel>
          <IonToggle
            checked={resolvedTheme === 'dark'}
            onIonChange={(e) => handleToggleChange(e.detail.checked)}
          />
          <IonButton
            fill="clear"
            size="small"
            onClick={handleThemeInfo}
            style={{ marginLeft: '8px' }}
          >
            <IonIcon icon={contrastOutline} />
          </IonButton>
        </IonItem>

        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header={t('themeOptions') || 'Theme Options'}
          message={t('themeOptionsDescription') || 'Choose your preferred theme appearance:\n\n• Light: Bright theme for daytime use\n• Dark: Dark theme for nighttime use\n• System: Automatically switch based on device settings'}
          buttons={[
            {
              text: t('gotIt') || 'Got It',
              role: 'cancel',
            },
          ]}
        />
      </>
    );
  }

  if (variant === 'select') {
    return (
      <IonItem>
        <IonIcon
          icon={getThemeIcon()}
          style={{ marginRight: '16px', color: 'var(--color-text-secondary)' }}
        />
        <IonLabel position="stacked">{t('theme') || 'Theme'}</IonLabel>
        <IonSelect
          value={theme}
          onIonChange={handleSelectChange}
          placeholder={t('selectTheme') || 'Select theme'}
        >
          <IonSelectOption value="light">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IonIcon icon={sunnyOutline} />
              {t('lightTheme') || 'Light'}
            </div>
          </IonSelectOption>
          <IonSelectOption value="dark">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IonIcon icon={moonOutline} />
              {t('darkTheme') || 'Dark'}
            </div>
          </IonSelectOption>
          <IonSelectOption value="system">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IonIcon icon={contrastOutline} />
              {t('systemTheme') || 'System'}
            </div>
          </IonSelectOption>
        </IonSelect>
      </IonItem>
    );
  }

  // Button variant
  return (
    <>
      <IonButton
        fill="clear"
        onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
      >
        <IonIcon icon={resolvedTheme === 'dark' ? sunnyOutline : moonOutline} />
        <IonLabel style={{ marginLeft: '8px' }}>
          {resolvedTheme === 'dark' ? t('lightTheme') : t('darkTheme')}
        </IonLabel>
      </IonButton>

      <IonAlert
        isOpen={showAlert}
        onDidDismiss={() => setShowAlert(false)}
        header={t('themeOptions') || 'Theme Options'}
        message={t('themeOptionsDescription') || 'Choose your preferred theme appearance:\n\n• Light: Bright theme for daytime use\n• Dark: Dark theme for nighttime use\n• System: Automatically switch based on device settings'}
        buttons={[
          {
            text: t('gotIt') || 'Got It',
            role: 'cancel',
          },
        ]}
      />
    </>
  );
};

export default ThemeToggle;